export const DEFAULT_PAGE_SIZE = 10;

export const Permissions = {
  AgentRead: 'agent:read',
  AgentCreateUpdate: 'agent:create_update',
  AgentDelete: 'agent:delete',
  AgentPublish: 'agent:publish',

  KnowledgeBaseRead: 'knowledge_base:read',
  KnowledgeBaseCreateUpdate: 'knowledge_base:create_update',
  KnowledgeBaseDelete: 'knowledge_base:delete',

  ToolsRead: 'tools:read',
  ToolsCreateUpdate: 'tools:create_update',
  ToolsDelete: 'tools:delete',

  PhoneNumberRead: 'phone_number:read',
  PhoneNumberCreateUpdate: 'phone_number:create_update',
  PhoneNumberDelete: 'phone_number:delete',

  ApiKeyRead: 'api_key:read',
  ApiKeyCreateUpdate: 'api_key:create',
  ApiKeyDelete: 'api_key:delete',

  BatchCallRead: 'batch_call:read',
  BatchCallCreateUpdate: 'batch_call:create_update',
  BatchCallDelete: 'batch_call:delete',

  CallsReadList: 'calls:read_list',
  CallsReadDetail: 'calls:read_detail',

  AuditLogsRead: 'audit_logs:read',

  UsersRead: 'users:read',
  UsersInvite: 'users:invite',
  UsersRemoveUser: 'users:remove_user',
  UsersUpdate: 'users:update',

  RolesRead: 'roles:read',
  RolesCreateUpdate: 'roles:create_update',
  RolesDelete: 'roles:delete',
};

// Knowledge base related constants
export const ALLOWED_FILE_TYPES = ['pdf', 'txt', 'md'];
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 10MB in bytes

export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
export const PREVIEWABLE_EXTENSIONS = [
  'pdf',
  'txt',
  'json',
  'csv',
  'md',
  'html',
  'htm',
  'xml',
  'log',
];
// Document upload initial state
export const INITIAL_DOCUMENT_UPLOAD_STATE = {
  documentType: 'file' as const,
  validationErrors: [],
  uploadedFile: null,
  isDragOver: false,
  webPageUrl: '',
  webPageError: null,
  textName: '',
  textContent: '',
  textError: null,
};

export const SAMPLE_TEMPLATE = [
  'full_name,phone_number,metadata',
  'John Doe,+12025550123,{"key": "value"}',
  'Jane Smith,+13105550456,{"key": "value"}',
].join('\n');

const TIMEZONE = {
  'Pacific/Midway': 'Midway Island, Samoa',
  'Pacific/Honolulu': 'Hawaii',
  'America/Juneau': 'Alaska',
  'America/Boise': 'Mountain Time',
  'America/Dawson': 'Dawson, Yukon',
  'America/Chihuahua': 'Chihuahua, La Paz, Mazatlan',
  'America/Phoenix': 'Arizona',
  'America/Los_Angeles': 'Pacific Time',
  'America/Chicago': 'Central Time',
  'America/Regina': 'Saskatchewan',
  'America/Mexico_City': 'Guadalajara, Mexico City, Monterrey',
  'America/Belize': 'Central America',
  'America/Detroit': 'Eastern Time',
  'America/Bogota': 'Bogota, Lima, Quito',
  'America/Caracas': 'Caracas, La Paz',
  'America/Santiago': 'Santiago',
  'America/St_Johns': 'Newfoundland and Labrador',
  'America/Sao_Paulo': 'Brasilia',
  'America/Tijuana': 'Tijuana',
  'America/Montevideo': 'Montevideo',
  'America/Argentina/Buenos_Aires': 'Buenos Aires, Georgetown',
  'America/Godthab': 'Greenland',
  'Atlantic/Azores': 'Azores',
  'Atlantic/Cape_Verde': 'Cape Verde Islands',
  GMT: 'UTC',
  'Europe/London': 'Edinburgh, London',
  'Europe/Dublin': 'Dublin',
  'Europe/Lisbon': 'Lisbon',
  'Africa/Casablanca': 'Casablanca, Monrovia',
  'Atlantic/Canary': 'Canary Islands',
  'Europe/Belgrade': 'Belgrade, Bratislava, Budapest, Ljubljana, Prague',
  'Europe/Sarajevo': 'Sarajevo, Skopje, Warsaw, Zagreb',
  'Europe/Brussels': 'Brussels, Copenhagen, Madrid, Paris',
  'Europe/Amsterdam': 'Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
  'Africa/Algiers': 'West Central Africa',
  'Europe/Bucharest': 'Bucharest',
  'Africa/Cairo': 'Cairo',
  'Europe/Helsinki': 'Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
  'Europe/Athens': 'Athens',
  'Asia/Jerusalem': 'Jerusalem',
  'Africa/Harare': 'Harare, Pretoria',
  'Europe/Moscow': 'Istanbul, Minsk, Moscow, St. Petersburg, Volgograd',
  'Asia/Kuwait': 'Kuwait, Riyadh',
  'Africa/Nairobi': 'Nairobi',
  'Asia/Baghdad': 'Baghdad',
  'Asia/Tehran': 'Tehran',
  'Asia/Dubai': 'Abu Dhabi, Muscat',
  'Asia/Baku': 'Baku, Tbilisi, Yerevan',
  'Asia/Kabul': 'Kabul',
  'Asia/Yekaterinburg': 'Ekaterinburg',
  'Asia/Karachi': 'Islamabad, Karachi, Tashkent',
  'Asia/Kolkata': 'Chennai, Kolkata, Mumbai, New Delhi',
  'Asia/Kathmandu': 'Kathmandu',
  'Asia/Dhaka': 'Astana, Dhaka',
  'Asia/Colombo': 'Sri Jayawardenepura',
  'Asia/Almaty': 'Almaty, Novosibirsk',
  'Asia/Rangoon': 'Yangon Rangoon',
  'Asia/Bangkok': 'Bangkok, Hanoi, Jakarta',
  'Asia/Krasnoyarsk': 'Krasnoyarsk',
  'Asia/Shanghai': 'Beijing, Chongqing, Hong Kong SAR, Urumqi',
  'Asia/Kuala_Lumpur': 'Kuala Lumpur, Singapore',
  'Asia/Taipei': 'Taipei',
  'Australia/Perth': 'Perth',
  'Asia/Irkutsk': 'Irkutsk, Ulaanbaatar',
  'Asia/Seoul': 'Seoul',
  'Asia/Tokyo': 'Osaka, Sapporo, Tokyo',
  'Asia/Yakutsk': 'Yakutsk',
  'Australia/Darwin': 'Darwin',
  'Australia/Adelaide': 'Adelaide',
  'Australia/Sydney': 'Canberra, Melbourne, Sydney',
  'Australia/Brisbane': 'Brisbane',
  'Australia/Hobart': 'Hobart',
  'Asia/Vladivostok': 'Vladivostok',
  'Pacific/Guam': 'Guam, Port Moresby',
  'Asia/Magadan': 'Magadan, Solomon Islands, New Caledonia',
  'Asia/Kamchatka': 'Kamchatka, Marshall Islands',
  'Pacific/Fiji': 'Fiji Islands',
  'Pacific/Auckland': 'Auckland, Wellington',
  'Pacific/Tongatapu': "Nuku'alofa",
};

export const TIMEZONE_OPTIONS = Object.entries(TIMEZONE).map(([value, label]) => ({
  value,
  label,
}));

// Starter prompt prefilled for the SENTIMENT post-call prompt.
export const SENTIMENT_STARTER_PROMPT = `You are a sentiment analysis expert. Determine the user's sentiment from the call transcript and explain your reasoning briefly.`;

export const JSON_TEMPLATES: Record<string, { label: string; description: string; value: string }> =
  {
    blank: {
      label: 'Blank',
      description: 'Start from scratch',
      value: JSON.stringify({ properties: [] }, null, 2),
    },
    patientInfo: {
      label: 'Patient info',
      description: 'Name, DOB, phone, insurance',
      value: JSON.stringify(
        {
          properties: [
            {
              identifier: 'patient_name',
              dataType: 'String',
              description: 'Full name of the patient as stated during the call.',
            },
            {
              identifier: 'date_of_birth',
              dataType: 'String',
              description: 'Patient date of birth in YYYY-MM-DD format.',
            },
            {
              identifier: 'phone_number',
              dataType: 'String',
              description: 'Contact phone number provided by the patient.',
            },
            {
              identifier: 'insurance_id',
              dataType: 'String',
              description: 'Insurance member ID mentioned during the call.',
            },
          ],
        },
        null,
        2
      ),
    },
    appointment: {
      label: 'Appointment',
      description: 'Date, provider, visit type, confirmed',
      value: JSON.stringify(
        {
          properties: [
            {
              identifier: 'appointment_date',
              dataType: 'String',
              description: 'Scheduled appointment date and time in ISO 8601 format.',
            },
            {
              identifier: 'provider_name',
              dataType: 'String',
              description: 'Name of the healthcare provider the appointment is with.',
            },
            {
              identifier: 'visit_type',
              dataType: 'String',
              description: 'Type of visit: in-person, telehealth, or phone.',
            },
            {
              identifier: 'confirmed',
              dataType: 'Boolean',
              description: 'Whether the patient confirmed the appointment during the call.',
            },
          ],
        },
        null,
        2
      ),
    },
    medication: {
      label: 'Medication',
      description: 'Name, dosage, frequency, adherence',
      value: JSON.stringify(
        {
          properties: [
            {
              identifier: 'medication_name',
              dataType: 'String',
              description: 'Name of the medication discussed.',
            },
            {
              identifier: 'dosage',
              dataType: 'String',
              description: 'Dosage amount and unit (e.g. 500mg).',
            },
            {
              identifier: 'frequency',
              dataType: 'String',
              description: 'How often the medication is taken (e.g. twice daily).',
            },
            {
              identifier: 'adherence',
              dataType: 'Boolean',
              description: 'Whether the patient reported taking the medication as prescribed.',
            },
            {
              identifier: 'side_effects',
              dataType: 'String',
              description: 'Any side effects reported by the patient.',
            },
          ],
        },
        null,
        2
      ),
    },
    callOutcome: {
      label: 'Call outcome',
      description: 'Outcome, duration, next steps',
      value: JSON.stringify(
        {
          properties: [
            {
              identifier: 'outcome',
              dataType: 'String',
              description: 'Overall call outcome: successful, voicemail, no-answer, escalated.',
            },
            {
              identifier: 'call_duration_seconds',
              dataType: 'Number',
              description: 'Total duration of the call in seconds.',
            },
            {
              identifier: 'patient_concern',
              dataType: 'String',
              description: 'Primary concern or reason the patient called.',
            },
            {
              identifier: 'next_steps',
              dataType: 'String',
              description: 'Agreed next steps or follow-up actions from the call.',
            },
            {
              identifier: 'escalated',
              dataType: 'Boolean',
              description: 'Whether the call was escalated to a human agent.',
            },
          ],
        },
        null,
        2
      ),
    },
  };

export const RECURRENCE_TYPE = [
  {
    label: 'Daily',
    value: 'day',
  },
  {
    label: 'Weekly',
    value: 'week',
  },
  {
    label: 'Monthly',
    value: 'month',
  },
];

export const WEEK_DAYS = [
  {
    label: 'M',
    value: 'MON',
  },
  {
    label: 'T',
    value: 'TUE',
  },
  {
    label: 'W',
    value: 'WED',
  },
  {
    label: 'T',
    value: 'THU',
  },
  {
    label: 'F',
    value: 'FRI',
  },
  {
    label: 'S',
    value: 'SAT',
  },
  {
    label: 'S',
    value: 'SUN',
  },
];

export const DAY_FULL: Record<string, string> = {
  MON: 'Mon',
  TUE: 'Tue',
  WED: 'Wed',
  THU: 'Thu',
  FRI: 'Fri',
  SAT: 'Sat',
  SUN: 'Sun',
};

export const MODULES = [
  {
    label: 'Agent',
    value: 'Agent',
  },
  {
    label: 'Auth',
    value: 'Auth',
  },
  {
    label: 'Call',
    value: 'Call',
  },
  {
    label: 'Call Settings',
    value: 'Call Settings',
  },
  {
    label: 'Keys',
    value: 'Keys',
  },
  {
    label: 'Knowledge Base',
    value: 'Knowledge Base',
  },
  {
    label: 'Prompt',
    value: 'Prompt',
  },
  {
    label: 'Roles',
    value: 'Roles',
  },
  {
    label: 'Scheduler',
    value: 'Scheduler',
  },
  {
    label: 'Telephony',
    value: 'Telephony',
  },
  {
    label: 'Tools',
    value: 'Tools',
  },
  {
    label: 'User',
    value: 'User',
  },
];

export const AGENT_STATUS = [
  {
    label: 'Draft',
    value: 'Draft',
  },
  {
    label: 'Published',
    value: 'Published',
  },
] as const;

export const BATCH_CALL_STATUS = [
  {
    label: 'Pending',
    value: 'Pending',
  },
  {
    label: 'Active',
    value: 'Active',
  },
  {
    label: 'Completed',
    value: 'Completed',
  },
  {
    label: 'Cancelled',
    value: 'Cancelled',
  },
] as const;

export const CALL_STATUS = [
  {
    label: 'Initiated',
    value: 'Initiated',
  },
  {
    label: 'Answered',
    value: 'Answered',
  },
  {
    label: 'Busy',
    value: 'Busy',
  },
  {
    label: 'Voicemail',
    value: 'Voicemail',
  },
  {
    label: 'Ongoing',
    value: 'Ongoing',
  },
  {
    label: 'Unanswered',
    value: 'Unanswered',
  },
  {
    label: 'Error',
    value: 'Error',
  },
] as const;

export const CALL_MODE = [
  {
    label: 'Inbound',
    value: 'Inbound',
  },
  {
    label: 'Outbound',
    value: 'Outbound',
  },
] as const;
